import AxiosInstance from '../../helpers/axios-instance/axios-instance';
import { InitiativeTasksConfig } from '../initiative-client/initiative-client.type';
import { appConfig } from '../../helpers/app-config/app-config';
import type { ExtendedEpic, MinifiedTask, SimpleEpic, Task } from './task-client.type';

const axiosInstance = AxiosInstance();
const baseURL = appConfig.VITE_APP_API_URL;

/**
 * Get epics for projects
 *
 * @param {string} portfolioId - The portfolio ID
 * @param {string[]} projectIds - An array of project IDs
 * @returns {Promise<SimpleEpic[]>} A promise that resolves to an array of simple epics
 */
function getEpicsForProjects(portfolioId?: string, projectIds?: string[]): Promise<SimpleEpic[]> {
  const url = new URL(baseURL + `/api/epics/`);
  const params = new URLSearchParams(url.search);
  if (portfolioId) {
    params.append('portfolio_id', portfolioId);
  }
  if (projectIds) {
    params.append('project_ids', projectIds?.join(',') || '');
  }
  url.search = params.toString();

  return axiosInstance
    .get(url.toString())
    .then((response) => response.data)
    .catch((error) => Promise.reject(error?.response?.data || 'Error while fetching epics'));
}

/**
 * Export tasks as a CSV file
 *
 * @param taskIds - An array of task IDs to export
 * @returns A Blob containing the CSV file
 */
function exportTasksAsCsv(taskIds: string[]): Promise<Blob> {
  const url = new URL(baseURL + `/api/tasks/export/`);

  return axiosInstance
    .post(url.toString(), { task_ids: taskIds }, { responseType: 'blob' })
    .then((response) => response.data)
    .catch((error) => Promise.reject(error?.response?.data || 'Error while exporting tasks'));
}

/**
 * Search for tasks
 *
 * @param {string} query - The search query
 * @param {string} organizationId - The organization ID
 * @param {string | null} projectId - The project ID
 * @param {string | null} boardId - The board ID
 * @param {string | null} workflowId - The workflow ID
 * @param {string | null} taskType - The task type
 * @param {string | null} startDate - The start date
 * @param {string | null} endDate - The end date
 * @returns {Promise<Task[]>} A promise that resolves to an array of tasks
 */
function searchTasks(
  query: string,
  organizationId: string,
  projectId?: string | null,
  boardId?: string | null,
  workflowId?: string | null,
  taskType?: string | null,
  startDate?: string | null,
  endDate?: string | null,
): Promise<Task[]> {
  const params: Record<string, string> = { query, organization_id: organizationId };

  if (projectId) {
    params.project_id = projectId;
  }

  if (boardId) {
    params.board_id = boardId;
  }

  if (workflowId) {
    params.workflow_id = workflowId;
  }

  if (taskType) {
    params.task_type = taskType;
  }

  if (startDate) {
    params.start_date = startDate;
  }

  if (endDate) {
    params.end_date = endDate;
  }

  const apiURL = baseURL + '/api/tasks/search/';
  return axiosInstance
    .get<Task[]>(apiURL, { params })
    .then((response) => response.data)
    .catch((error) => {
      return Promise.reject(error.response || 'There was a problem searching for tasks');
    });
}

/**
 * Fetches an epic
 *
 * @param {string} epicId - The epic ID
 * @returns {Promise<ExtendedEpic>} The epic
 */
function getEpic(epicId: string): Promise<ExtendedEpic> {
  const apiURL = baseURL + `/api/epics/${epicId}/`;
  return axiosInstance
    .get(apiURL)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error.response || 'There was a problem fetching the epic'));
}

/**
 * Fetches epic tasks
 *
 * @param {string} epicId - The epic ID
 * @param {InitiativeTasksConfig} filters - Filters to apply to the task query
 * @returns {Promise<MinifiedTask[]>} The epic tasks
 */
function getEpicTasks(epicId: string, filters: InitiativeTasksConfig): Promise<MinifiedTask[]> {
  const apiURL = baseURL + `/api/epics/${epicId}/tasks/`;

  return axiosInstance
    .get(apiURL, { params: filters })
    .then((response) => response.data)
    .catch((error) => Promise.reject(error.response || 'There was a problem fetching the epic tasks'));
}

export { exportTasksAsCsv, getEpic, getEpicsForProjects, getEpicTasks, searchTasks };
